/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { PageConfig_EnrolledProductsComponent as EnrolledProductsComponent } from '__generated__/graphql-types-do-not-use';

import { Grid, Typography2, breakpoints } from '@coursera/cds-core';

import CourseCard from 'bundles/course-cards/components/course-card/enterprise/CourseCard';
import EnterpriseContinueLearningCourseCardNotification from 'bundles/page-config-common/components/enterprise/EnterpriseContinueLearningCourseCardNotification';
import ContinueLearningModule from 'bundles/page-config-common/components/shared/ContinueLearningModule';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import NavigatorSelector from 'bundles/page-config-common/components/shared/NavigatorSelector';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import { useEnterpriseInProgressCourse } from 'bundles/page-config-common/contexts/enterprise/EnterpriseEnrolledCourseContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { getEnterpriseMyLearningLinks } from 'bundles/unified-home-common/utils/getEnterpriseMyLearningLinks';

import _t from 'i18n!nls/consumer-home';

const styles = {
  navigation: css`
    margin-top: var(--cds-spacing-600);
    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-100);
    }
  `,
  titleEnterprise: css`
    padding-bottom: var(--cds-spacing-200);
  `,
  titleEnterpriseHomeRefresh: css`
    padding-bottom: var(--cds-spacing-150);
  `,
  titleConsumer: css`
    margin-top: var(--cds-spacing-50);
    padding-bottom: var(--cds-spacing-200);
  `,
};

const EnterpriseContinueLearningModuleRenderer = ({
  pageConfigComponent,
}: {
  pageConfigComponent: EnrolledProductsComponent;
}) => {
  const program = useProgram();
  const thirdPartyOrg = useThirdPartyOrganization();
  const { inProgressCourse, courseDetails } = useEnterpriseInProgressCourse();
  const features = useEnterpriseProductFeatures();
  const { currentLevel } = useHeaderLevel();

  if (!program || !thirdPartyOrg || !inProgressCourse) {
    return null;
  }

  const enterpriseLinks = getEnterpriseMyLearningLinks(program?.metadata.slug, features?.enableSkillsDashboard);

  return (
    <HomeSection className="PageConfig_EnrolledProductsComponent" onlyTopPadding={true}>
      <ContinueLearningModule
        isEnterprise
        key={pageConfigComponent.id}
        title={
          <Typography2 css={styles.titleEnterpriseHomeRefresh} variant="titleXSmall" component={currentLevel}>
            {_t('Continue learning')}
          </Typography2>
        }
        courseCard={
          <Grid item xs={12} md={10}>
            <CourseCard
              courseId={inProgressCourse.courseId}
              programId={program.id}
              isEnrolled={inProgressCourse.isEnrolled}
              canUnenroll={inProgressCourse.canUnenroll}
              isGrouped
              thirdPartyOrganizationId={thirdPartyOrg.id}
            />
          </Grid>
        }
        navigation={
          <Grid item xs={12} md={2}>
            <NavigatorSelector links={enterpriseLinks} />
          </Grid>
        }
        notification={
          courseDetails ? (
            <EnterpriseContinueLearningCourseCardNotification previouslyCompletedCourse={courseDetails} />
          ) : undefined
        }
      />
    </HomeSection>
  );
};

export default EnterpriseContinueLearningModuleRenderer;
